import axios from 'axios';
import jwtInterceptor from './jwtInterceptor';
import errorInterceptor from './errorInterceptor';

/**
 * Per-user AI memory (Profile > "Memoria AI"): a free-text note the backend
 * injects as a system message into every LLM request this user makes — the
 * guide drawer, the BPMN modeller, the robot builder and the AI Assistant
 * alike.
 *
 * The user id never travels in the payload: the backend reads it from the
 * JWT, so nobody can read or overwrite somebody else's memory.
 */
const instance = axios.create({
  baseURL: `${process.env.REACT_APP_STATS_BACKEND_BASE_URI}/ai/memory`,
  headers: {
    "Content-Type": "application/json"
  }
});
instance.interceptors.request.use(jwtInterceptor);
instance.interceptors.response.use(undefined, errorInterceptor);

export interface UserMemory {
  memory: string;
  enabled: boolean;
  updated_at: string | null;
  updated_by_ai: boolean;
  /** Hard cap enforced by the backend and by the DB CHECK constraint. */
  max_chars: number;
}

export interface EnhancedUserMemory {
  enhanced: string;
  max_chars: number;
}

export const getUserMemory = (): Promise<UserMemory> =>
  instance.get<UserMemory>("").then((r) => r.data);

export const saveUserMemory = (
  memory: string,
  enabled: boolean
): Promise<UserMemory> =>
  instance.put<UserMemory>("", { memory, enabled }).then((r) => r.data);

/**
 * Rewrites the text with the cheap `memory_enhance` model. Deliberately does
 * NOT persist: the caller shows the proposal next to the original and only
 * `saveUserMemory` commits it.
 */
export const enhanceUserMemory = (
  memory: string,
  organizationId?: number | string
): Promise<EnhancedUserMemory> =>
  instance
    .post<EnhancedUserMemory>("/enhance", {
      memory,
      ...(organizationId ? { organizationId: Number(organizationId) } : {}),
    })
    .then((r) => r.data);

export default instance;
